import { useId, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { GlassCard } from '@/components/ui/GlassCard'
import type { FaqItem } from '@/lib/faq'

/**
 * Accessible single-source accordion (master prompt §5.6: buttons inside
 * headings, aria-expanded/aria-controls, keyboard support, one data source
 * — no separate desktop/mobile duplicated markup like the live site's
 * inline + overlay copies).
 */
export function FaqAccordion({ items }: { items: FaqItem[] }) {
  const { lang } = useLanguage()
  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null)

  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => (
        <FaqRow
          key={item.id}
          item={item}
          lang={lang}
          open={openId === item.id}
          onToggle={() => setOpenId((cur) => (cur === item.id ? null : item.id))}
        />
      ))}
    </div>
  )
}

function FaqRow({
  item,
  lang,
  open,
  onToggle,
}: {
  item: FaqItem
  lang: 'bs' | 'en'
  open: boolean
  onToggle: () => void
}) {
  const panelId = useId()

  return (
    <GlassCard className="overflow-hidden">
      <h3>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold text-foreground transition-colors hover:text-accent-fire md:text-base"
        >
          <span>{lang === 'en' ? item.qEn : item.q}</span>
          <ChevronDown
            size={18}
            className={`shrink-0 text-foreground-muted transition-transform duration-normal ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </h3>
      {open && (
        <div id={panelId} className="px-5 pb-5">
          <p
            className="text-sm leading-relaxed text-foreground-muted [&_strong]:font-semibold [&_strong]:text-foreground-subtle"
            dangerouslySetInnerHTML={{ __html: lang === 'en' ? item.aEn : item.a }}
          />
        </div>
      )}
    </GlassCard>
  )
}
